import { Component, ElementRef, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { faIconClass, iconBareName, isFaIcon } from 'src/app/core/helpers/ui/quick-access-icon';
import { elevatedSlotIndex } from 'src/app/core/helpers/ui/quick-access-layout';
import {
  MAX_QUICK_ACCESS_ITEMS,
  QUICK_ACCESS_DRAG_START_DELAY,
  QUICK_ACCESS_FA_ICON_CHOICES,
  QUICK_ACCESS_ICON_CHOICES,
  QUICK_ACCESS_LIMIT_NOTICE_MS,
  QUICK_ACCESS_NAV_MODE_BOTTOM_BAR,
  QUICK_ACCESS_NAV_MODE_FLOATING_BUTTON,
  QUICK_ACCESS_NAV_MODE_HIDDEN,
  QUICK_ACCESS_TRAY_LIST_ID,
} from 'src/app/core/helpers/ui/quick-access.constant';
import {
  QuickAccessCustomizerResult,
  QuickAccessNavMode,
  QuickAccessPage,
  QuickAccessTopGroup,
} from 'src/app/core/interfaces/ui/quick-access.interface';

@Component({
  selector: 'shared-quick-access-customizer',
  templateUrl: './quick-access-customizer.component.html',
  styleUrls: ['./quick-access-customizer.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class QuickAccessCustomizerComponent implements OnInit, OnDestroy {
  @Input() catalog: QuickAccessPage[] = [];
  @Input() selectedIds: (string | null)[] = [];
  @Input() defaultIds: (string | null)[] = [];
  @Input() customIcons: Record<string, string> = {};
  @Input() customLabels: Record<string, string> = {};
  @Input() navMode: QuickAccessNavMode = QUICK_ACCESS_NAV_MODE_BOTTOM_BAR;

  public groups: QuickAccessTopGroup[] = [];
  public slots: (QuickAccessPage | null)[] = [];

  private searchIndex = new Map<string, string>();
  private iconChanges: Record<string, string | null> = {};
  private labelChanges: Record<string, string | null> = {};

  public armedPageId: string | null = null;
  public filteredIconChoices: string[] = [];

  public searchTerm: string = '';
  public editingValue: string = '';
  public iconSearchTerm: string = '';

  public readonly selectedTrayId = QUICK_ACCESS_TRAY_LIST_ID;
  public readonly maxSelected: number = MAX_QUICK_ACCESS_ITEMS;
  public readonly dragStartDelay = QUICK_ACCESS_DRAG_START_DELAY;
  public readonly navModeBottomBar = QUICK_ACCESS_NAV_MODE_BOTTOM_BAR;
  public readonly navModeHidden = QUICK_ACCESS_NAV_MODE_HIDDEN;
  public readonly navModeFloatingButton = QUICK_ACCESS_NAV_MODE_FLOATING_BUTTON;

  private justDragged: boolean = false;

  private dragStartY: number = 0;
  private currentTranslateY: number = 0;
  private traySlotCenters: number[] = [];
  public pickedIndex: number | null = null;
  public droppedIndex: number | null = null;
  public armedSlotIndex: number | null = null;
  public iconPickerIndex: number | null = null;

  public limitReached: boolean = false;
  private isSwipingDown: boolean = false;

  public shiftedIndexes = new Set<number>();
  private armTimer?: ReturnType<typeof setTimeout>;
  private shiftAnimationTimer?: ReturnType<typeof setTimeout>;
  private limitNoticeTimer?: ReturnType<typeof setTimeout>;

  private boundMouseUp = this.onDocMouseUp.bind(this);
  private boundMouseMove = this.onDocMouseMove.bind(this);

  private readonly allIconChoices = [...QUICK_ACCESS_ICON_CHOICES, ...QUICK_ACCESS_FA_ICON_CHOICES];

  constructor(
    public activeModal: NgbActiveModal,
    private elementRef: ElementRef<HTMLElement>,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.slots = this.buildSlots();
    this.groups = this.buildGroups();
    this.searchIndex = this.buildSearchIndex();
  }

  ngOnDestroy(): void {
    clearTimeout(this.armTimer);
    clearTimeout(this.shiftAnimationTimer);
    clearTimeout(this.limitNoticeTimer);
    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);
  }

  get selectedPages(): QuickAccessPage[] {
    return this.slots.filter((page): page is QuickAccessPage => !!page);
  }

  get demoLang(): string {
    return (this.translate.currentLang || this.translate.getDefaultLang() || '').toUpperCase();
  }

  get hasSearchResults(): boolean {
    return this.groups.some((topGroup) =>
      topGroup.subGroups.some((subGroup) => subGroup.pages.length)
    );
  }

  public onSearchChange(term: string): void {
    this.searchTerm = term;
    this.groups = this.buildGroups();
  }

  public clearSearch(): void {
    this.onSearchChange('');
  }

  public selectNavMode(mode: QuickAccessNavMode): void {
    this.navMode = mode;
  }

  public resetToDefaults(): void {
    this.disarm();
    this.pickedIndex = null;
    this.iconPickerIndex = null;

    this.slots = this.defaultIds.map((id) =>
      id ? (this.catalog.find((page) => page.id === id) ?? null) : null
    );

    this.labelChanges = this.clearedOverrides(this.customLabels);
    this.iconChanges = this.clearedOverrides(this.customIcons);

    this.customLabels = {};
    this.customIcons = {};

    this.navMode = QUICK_ACCESS_NAV_MODE_BOTTOM_BAR;
  }

  private clearedOverrides(current: Record<string, string>): Record<string, string | null> {
    return Object.keys(current).reduce<Record<string, string | null>>((cleared, pageId) => {
      cleared[pageId] = null;
      return cleared;
    }, {});
  }

  public groupListId(groupId: string): string {
    return `qa-group-${groupId}`;
  }

  public isSelected(page: QuickAccessPage): boolean {
    return this.slots.some((slot) => slot?.id === page.id);
  }

  public onDragEnded(): void {
    this.justDragged = true;
    this.disarm();
    setTimeout(() => (this.justDragged = false), 0);
  }

  public armPage(page: QuickAccessPage): void {
    if (this.isSelected(page)) return;
    this.startArmTimer(() => (this.armedPageId = page.id));
  }

  public armSlot(index: number): void {
    if (!this.slots[index]) return;
    this.startArmTimer(() => (this.armedSlotIndex = index));
  }

  public disarm(): void {
    clearTimeout(this.armTimer);
    this.armedPageId = null;
    this.armedSlotIndex = null;
  }

  private startArmTimer(arm: () => void): void {
    clearTimeout(this.armTimer);
    this.armTimer = setTimeout(arm, this.dragStartDelay.touch);
  }

  public captureTraySlotCenters(): void {
    this.disarm();

    const trayItems = this.elementRef.nativeElement.querySelectorAll<HTMLElement>(
      '.qa-tray-bar > .qa-tray-item'
    );

    this.traySlotCenters = Array.from(trayItems).map((el) => {
      const rect = el.getBoundingClientRect();
      return rect.left + rect.width / 2;
    });
  }

  get elevatedIndex(): number | null {
    return elevatedSlotIndex(this.slots);
  }

  get isFull(): boolean {
    return this.selectedPages.length >= this.maxSelected;
  }

  public toggle(page: QuickAccessPage): void {
    if (this.isSelected(page)) {
      this.clearPage(page);
      return;
    }

    const emptyIndex = this.slots.findIndex((slot) => slot === null);
    if (emptyIndex === -1) {
      this.showLimitNotice();
      return;
    }
    this.setSlot(emptyIndex, this.withCustomizations(page));
  }

  private showLimitNotice(): void {
    clearTimeout(this.limitNoticeTimer);
    this.limitReached = true;
    this.limitNoticeTimer = setTimeout(
      () => (this.limitReached = false),
      QUICK_ACCESS_LIMIT_NOTICE_MS
    );
  }

  public removeAt(index: number): void {
    this.setSlot(index, null);
    if (this.pickedIndex === index) this.pickedIndex = null;
  }

  public openSlotEditor(index: number): void {
    const slot = this.slots[index];
    if (!slot) return;

    this.pickedIndex = null;
    this.iconPickerIndex = index;
    this.editingValue = slot.customLabel || this.translate.instant(slot.titleKey);
    this.onIconSearchChange('');
  }

  public closeSlotEditor(): void {
    const index = this.iconPickerIndex;
    this.iconPickerIndex = null;
    if (index === null) return;

    const slot = this.slots[index];
    if (!slot) return;

    const trimmed = this.editingValue.trim();
    const defaultLabel = this.translate.instant(slot.titleKey);
    const customLabel = trimmed && trimmed !== defaultLabel ? trimmed : undefined;

    if ((slot.customLabel ?? null) === (customLabel ?? null)) return;

    this.labelChanges[slot.id] = customLabel ?? null;
    this.setSlot(index, { ...slot, customLabel });
  }

  public onIconSearchChange(term: string): void {
    this.iconSearchTerm = term;

    const terms = this.normalize(term).split(/\s+/).filter(Boolean);
    if (!terms.length) {
      this.filteredIconChoices = this.allIconChoices;
      return;
    }

    this.filteredIconChoices = this.allIconChoices.filter((icon) => {
      const name = this.normalize(iconBareName(icon).replace(/-/g, ' '));
      return terms.every((t) => name.includes(t));
    });
  }

  public isFaIcon(icon: string): boolean {
    return isFaIcon(icon);
  }

  public faIconClass(icon: string): string {
    return faIconClass(icon);
  }

  public get iconPickerSlot(): QuickAccessPage | null {
    return this.iconPickerIndex === null ? null : this.slots[this.iconPickerIndex];
  }

  public isCurrentIcon(icon: string): boolean {
    const slot = this.iconPickerSlot;
    return !!slot && (slot.customIcon ?? slot.groupIcon) === icon;
  }

  public selectIcon(icon: string): void {
    const index = this.iconPickerIndex;
    if (index === null) return;

    const slot = this.slots[index];
    if (!slot) return;

    const customIcon = icon === slot.groupIcon ? undefined : icon;

    this.iconChanges[slot.id] = customIcon ?? null;
    this.setSlot(index, { ...slot, customIcon });
  }

  public resetIcon(): void {
    const index = this.iconPickerIndex;
    if (index === null) return;

    const slot = this.slots[index];
    if (!slot) return;

    this.iconChanges[slot.id] = null;
    this.setSlot(index, { ...slot, customIcon: undefined });
  }

  public onSlotTap(index: number): void {
    if (this.justDragged) return;

    if (this.pickedIndex === null) {
      if (this.slots[index]) this.pickedIndex = index;
      return;
    }

    if (this.pickedIndex === index) {
      this.pickedIndex = null;
      return;
    }

    const picked = this.slots[this.pickedIndex];
    const target = this.slots[index];
    this.setSlot(this.pickedIndex, target);
    this.setSlot(index, picked);
    this.pickedIndex = null;
  }

  public onTrayDropped(event: CdkDragDrop<(QuickAccessPage | null)[]>): void {
    const page: QuickAccessPage = event.item.data;
    const targetIndex = this.resolveDropIndex(event.dropPoint?.x, event.currentIndex);

    if (event.previousContainer === event.container) {
      const sourceIndex = this.slots.findIndex((slot) => slot?.id === page.id);
      if (sourceIndex === -1 || sourceIndex === targetIndex) return;
      const displaced = this.slots[targetIndex];
      this.setSlot(targetIndex, page);
      this.setSlot(sourceIndex, displaced);
      this.playShiftAnimation(targetIndex, displaced ? [sourceIndex] : []);
      return;
    }

    this.insertWithShift(targetIndex, this.withCustomizations(page));
  }

  private withCustomizations(page: QuickAccessPage): QuickAccessPage {
    const customLabel = this.customLabels[page.id];
    const customIcon = this.customIcons[page.id];
    if (!customLabel && !customIcon) return page;

    return {
      ...page,
      ...(customLabel ? { customLabel } : {}),
      ...(customIcon ? { customIcon } : {}),
    };
  }

  private insertWithShift(targetIndex: number, page: QuickAccessPage): void {
    const next = [...this.slots];
    const shiftedIndexes: number[] = [];
    let carried: QuickAccessPage | null = page;

    for (let i = targetIndex; i < this.maxSelected && carried; i++) {
      const displaced = next[i];
      next[i] = carried;
      if (i !== targetIndex) shiftedIndexes.push(i);
      carried = displaced;
    }

    if (carried) {
      const fallbackIndex = next.findIndex((slot) => slot === null);
      if (fallbackIndex !== -1) {
        next[fallbackIndex] = carried;
        shiftedIndexes.push(fallbackIndex);
      }
    }

    this.slots = next;
    this.playShiftAnimation(targetIndex, shiftedIndexes);
  }

  private playShiftAnimation(droppedIndex: number, shiftedIndexes: number[]): void {
    this.droppedIndex = droppedIndex;
    this.shiftedIndexes = new Set(shiftedIndexes);

    clearTimeout(this.shiftAnimationTimer);
    this.shiftAnimationTimer = setTimeout(() => {
      this.droppedIndex = null;
      this.shiftedIndexes = new Set();
    }, 300);
  }

  private resolveDropIndex(dropX: number | undefined, fallback: number): number {
    if (dropX === undefined || this.traySlotCenters.length === 0) return fallback;

    let closestIndex = fallback;
    let closestDistance = Infinity;

    this.traySlotCenters.forEach((centerX, index) => {
      const distance = Math.abs(dropX - centerX);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  public confirm(): void {
    this.closeSlotEditor();

    const result: QuickAccessCustomizerResult = {
      ids: this.slots.map((page) => page?.id ?? null),
      labelChanges: this.labelChanges,
      iconChanges: this.iconChanges,
      navMode: this.navMode,
    };
    this.activeModal.close(result);
  }

  public cancel(): void {
    this.activeModal.dismiss();
  }

  public onHandleTouchStart(event: TouchEvent): void {
    if (event.touches.length !== 1) return;
    const target = event.target as HTMLElement;
    if (target?.closest('.btn-close') || target?.closest('input') || target?.closest('button'))
      return;
    this.startSwipe(event.touches[0].clientY);
  }

  public onHandleTouchMove(event: TouchEvent): void {
    if (!this.isSwipingDown || event.touches.length !== 1) return;
    const deltaY = event.touches[0].clientY - this.dragStartY;
    this.updateSwipe(deltaY);
  }

  public onHandleTouchEnd(): void {
    if (!this.isSwipingDown) return;
    this.endSwipe();
  }

  public onHandleMouseDown(event: MouseEvent): void {
    if (event.button !== 0) return;
    const target = event.target as HTMLElement;
    if (target?.closest('.btn-close') || target?.closest('input') || target?.closest('button'))
      return;
    this.startSwipe(event.clientY);
    document.addEventListener('mousemove', this.boundMouseMove);
    document.addEventListener('mouseup', this.boundMouseUp);
  }

  private onDocMouseMove(event: MouseEvent): void {
    if (!this.isSwipingDown) return;
    const deltaY = event.clientY - this.dragStartY;
    this.updateSwipe(deltaY);
  }

  private onDocMouseUp(): void {
    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);
    this.endSwipe();
  }

  private startSwipe(clientY: number): void {
    this.isSwipingDown = true;
    this.dragStartY = clientY;
    this.currentTranslateY = 0;
    const innerSheet = this.elementRef.nativeElement.querySelector('.qa-sheet') as HTMLElement;
    if (innerSheet) {
      innerSheet.style.transform = '';
    }
    const sheet = this.getSheetElement();
    if (sheet) {
      sheet.style.transition = 'none';
    }
  }

  private updateSwipe(deltaY: number): void {
    const sheet = this.getSheetElement();
    if (!sheet) return;
    if (deltaY > 0) {
      this.currentTranslateY = deltaY;
      sheet.style.transform = `translateY(${deltaY}px)`;
    } else {
      this.currentTranslateY = 0;
      sheet.style.transform = 'translateY(0)';
    }
  }

  private endSwipe(): void {
    this.isSwipingDown = false;
    const sheet = this.getSheetElement();
    if (!sheet) return;

    if (this.currentTranslateY > 70) {
      sheet.style.transition = 'transform 0.22s ease-out';
      sheet.style.transform = 'translateY(100%)';
      setTimeout(() => {
        this.cancel();
      }, 220);
    } else {
      sheet.style.transition = 'transform 0.2s cubic-bezier(0.2, 0, 0, 1)';
      sheet.style.transform = 'translateY(0)';
    }
  }

  private getSheetElement(): HTMLElement | null {
    return (
      (this.elementRef.nativeElement.closest('.quick-access-sheet-dialog') as HTMLElement) ||
      (this.elementRef.nativeElement.closest('.modal-dialog') as HTMLElement) ||
      (this.elementRef.nativeElement.closest('.modal-content') as HTMLElement) ||
      (this.elementRef.nativeElement.querySelector('.qa-sheet') as HTMLElement)
    );
  }

  private setSlot(index: number, page: QuickAccessPage | null): void {
    this.slots = this.slots.map((slot, i) => (i === index ? page : slot));
  }

  private clearPage(page: QuickAccessPage): void {
    this.slots = this.slots.map((slot) => (slot?.id === page.id ? null : slot));
  }

  private buildSlots(): (QuickAccessPage | null)[] {
    return Array.from({ length: this.maxSelected }, (_, index) => {
      const id = this.selectedIds[index];
      if (!id) return null;

      const page = this.catalog.find((candidate) => candidate.id === id);
      return page ? this.withCustomizations(page) : null;
    });
  }

  private buildSearchIndex(): Map<string, string> {
    const index = new Map<string, string>();

    for (const page of this.catalog) {
      const haystack = [page.titleKey, page.groupTitleKey, page.topGroupTitleKey]
        .map((key) => this.translate.instant(key))
        .join(' ');
      index.set(page.id, this.normalize(haystack));
    }

    return index;
  }

  private normalize(value: string): string {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '');
  }

  private matchesSearch(page: QuickAccessPage, terms: string[]): boolean {
    if (!terms.length) return true;
    const haystack = this.searchIndex.get(page.id) ?? '';
    return terms.every((term) => haystack.includes(term));
  }

  private buildGroups(): QuickAccessTopGroup[] {
    const terms = this.normalize(this.searchTerm).split(/\s+/).filter(Boolean);
    const topGroups = new Map<string, QuickAccessTopGroup>();

    for (const page of this.catalog) {
      if (!this.matchesSearch(page, terms)) continue;

      let topGroup = topGroups.get(page.topGroupId);
      if (!topGroup) {
        topGroup = {
          topGroupId: page.topGroupId,
          topGroupTitleKey: page.topGroupTitleKey,
          subGroups: [],
        };
        topGroups.set(page.topGroupId, topGroup);
      }

      let subGroup = topGroup.subGroups.find((g) => g.groupId === page.groupId);
      if (!subGroup) {
        subGroup = { groupId: page.groupId, groupTitleKey: page.groupTitleKey, pages: [] };
        topGroup.subGroups.push(subGroup);
      }
      subGroup.pages.push(page);
    }

    return [...topGroups.values()];
  }
}
